import { range } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import {
  MostRecentAssessmentAnswerNode,
  OverallGoalAssessmentQuestionNode,
  OverallRecognitionCoreValueAssessmentQuestionNode,
  RangeAssessmentQuestionNode,
  TalentIndicatorAssessmentQuestionNode,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Tooltip from "@components/tooltip/tooltip";
import {
  TopicflowIcon,
  TopicflowIconType,
} from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

const PrevAnswerArrow = ({
  prevAnswer,
  question,
  currentIntegerAnswer,
}: {
  question:
    | Pick<
        TalentIndicatorAssessmentQuestionNode,
        "__typename" | "labels" | "startValue" | "endValue"
      >
    | Pick<
        RangeAssessmentQuestionNode,
        "__typename" | "labels" | "startValue" | "endValue"
      >
    | Pick<
        OverallGoalAssessmentQuestionNode,
        "__typename" | "labels" | "startValue" | "endValue"
      >
    | Pick<
        OverallRecognitionCoreValueAssessmentQuestionNode,
        "__typename" | "labels" | "startValue" | "endValue"
      >;
  prevAnswer?: Pick<
    MostRecentAssessmentAnswerNode,
    "integerAnswer" | "submitted"
  >;
  currentIntegerAnswer: number;
}) => {
  const label = useLabel();
  const answerLabels = useMemo(
    () =>
      range(question.startValue, question.endValue + 1).reduce(
        (acc, value, index) => ({
          ...acc,
          [value]: question.labels[index] || value.toString(),
        }),
        {} as Record<string, string>
      ),
    [question]
  );
  const icon = useMemo(() => {
    if (!prevAnswer?.integerAnswer) {
      return null;
    }
    const prevIntegerAnswer = prevAnswer.integerAnswer;
    const iconType: TopicflowIconType =
      prevIntegerAnswer < currentIntegerAnswer
        ? "upRightArrow"
        : prevIntegerAnswer > currentIntegerAnswer
        ? "downRightArrow"
        : "equals";
    const iconColor =
      prevIntegerAnswer < currentIntegerAnswer
        ? "text-emerald-500"
        : prevIntegerAnswer > currentIntegerAnswer
        ? "text-red-700"
        : "text-gray-300";
    return (
      <TopicflowIcon
        type={iconType}
        className={classNames("h-4 w-4", iconColor)}
      />
    );
  }, [currentIntegerAnswer, prevAnswer]);

  if (!prevAnswer?.integerAnswer) {
    return null;
  }
  return (
    <Tooltip
      text={`Last ${label("review", { capitalize: true })} Score: ${
        answerLabels[prevAnswer.integerAnswer]
      }<br/>(${moment(prevAnswer.submitted).format("MMM Do, YYYY")})`}
    >
      <div className="shrink-0">{icon}</div>
    </Tooltip>
  );
};

export default PrevAnswerArrow;
